export const amountToCents = (value) => {
    if (value === undefined || value === null || value === '') return 0;

    const normalized = String(value)
        .trim()
        .replace(/[\s\u00a0]/g, '')
        .replace(',', '.');

    if (!/^\d+(?:\.\d{0,2})?$/.test(normalized)) return null;

    const amount = Number(normalized);
    if (!Number.isFinite(amount) || amount < 0) return null;
    return Math.round(amount * 100);
};

export const getPostingTotals = (rows = []) => rows.reduce((totals, row) => {
    const debit = amountToCents(row.debit);
    const credit = amountToCents(row.credit);

    return {
        debitCents: totals.debitCents + (debit === null ? 0 : debit),
        creditCents: totals.creditCents + (credit === null ? 0 : credit),
    };
}, { debitCents: 0, creditCents: 0 });

export const formatLocalDate = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export const findFiscalYearForDate = (fiscalYears = [], date) => {
    const localDate = typeof date === 'string' ? date : formatLocalDate(date);
    if (!localDate) return null;

    return fiscalYears.find((fiscalYear) => {
        const start = String(fiscalYear.start_date || '').slice(0, 10);
        const end = String(fiscalYear.end_date || '').slice(0, 10);
        return start && end && start <= localDate && localDate <= end;
    }) || null;
};

export const rowsFromCopiedVerification = (verification, timestamp = Date.now()) => {
    const rows = (verification?.transactions || []).map((transaction, index) => ({
        id: `${timestamp}-${index}`,
        account_id: transaction.account_number || '',
        account_number: transaction.account_number || '',
        account_name: transaction.account_name || '',
        debit: transaction.transaction_type === 'debit'
            ? String(transaction.amount ?? '').replace('.', ',')
            : '',
        credit: transaction.transaction_type === 'credit'
            ? String(transaction.amount ?? '').replace('.', ',')
            : '',
    }));

    while (rows.length < 2) {
        rows.push({
            id: `${timestamp}-${rows.length}`,
            account_id: '',
            account_number: '',
            account_name: '',
            debit: '',
            credit: '',
        });
    }

    return rows;
};

export const getManualEntryIssues = ({ description, rows, fiscalYear }) => {
    const issues = [];

    if (!description || !description.trim()) {
        issues.push({ key: 'description', message: 'Ange en beskrivning.' });
    }

    if (!fiscalYear) {
        issues.push({ key: 'fiscalYear', message: 'Datumet tillhör inget räkenskapsår.' });
    } else if (fiscalYear.state === 'closed') {
        issues.push({ key: 'fiscalYear', message: `Räkenskapsår ${fiscalYear.year} är låst.` });
    }

    (rows || []).forEach((row, index) => {
        if (!row.account_id) {
            issues.push({ key: `row-${index}-account`, row: index, message: `Rad ${index + 1}: välj ett konto.` });
        }

        const debit = amountToCents(row.debit);
        const credit = amountToCents(row.credit);
        if (debit === null || credit === null) {
            issues.push({ key: `row-${index}-amount`, row: index, message: `Rad ${index + 1}: ange ett giltigt belopp med högst två decimaler.` });
        } else if (row.template_side === 'debit' && credit > 0) {
            issues.push({ key: `row-${index}-amount`, row: index, message: `Rad ${index + 1}: mallen kräver att beloppet bokförs i debet.` });
        } else if (row.template_side === 'credit' && debit > 0) {
            issues.push({ key: `row-${index}-amount`, row: index, message: `Rad ${index + 1}: mallen kräver att beloppet bokförs i kredit.` });
        } else if (debit <= 0 && credit <= 0) {
            issues.push({ key: `row-${index}-amount`, row: index, message: `Rad ${index + 1}: ange debet eller kredit.` });
        } else if (debit > 0 && credit > 0) {
            issues.push({ key: `row-${index}-amount`, row: index, message: `Rad ${index + 1}: använd bara debet eller kredit.` });
        }
    });

    const { debitCents, creditCents } = getPostingTotals(rows);
    if (debitCents !== creditCents) {
        issues.push({ key: 'balance', message: 'Debet och kredit måste balansera.' });
    }

    return issues;
};

const VAT_RECEIVABLE_ACCOUNTS = new Set([
    '2640', '2641', '2642', '2643', '2644',
    '2645', '2646', '2647', '2648', '2649',
]);

export const resolveTemplatePostingSide = (templateType, accountNumber, declaredSide = 'auto') => {
    if (declaredSide === 'debit' || declaredSide === 'credit') return declaredSide;
    if (declaredSide !== 'auto') return null;

    const normalizedAccount = String(accountNumber || '');
    if (!/^\d{4,5}$/.test(normalizedAccount)) return null;
    const accountClass = Number(normalizedAccount.charAt(0));

    if (templateType === 'income') {
        if (/^(37|39)\d{2}$/.test(normalizedAccount)) return null;
        if (accountClass === 1) return 'debit';
        if (accountClass === 2 || accountClass === 3) return 'credit';
        if (accountClass >= 4 && accountClass <= 7) return 'debit';
        return null;
    }

    if (templateType === 'expense') {
        if (accountClass === 1) return 'credit';
        if (accountClass === 2) {
            return VAT_RECEIVABLE_ACCOUNTS.has(normalizedAccount) ? 'debit' : 'credit';
        }
        if (accountClass >= 4 && accountClass <= 7) return 'debit';
    }

    return null;
};

export const getTemplateAccountPreview = (template, accounts = []) => {
    const bodyInputs = template?.body?.inputs || [];
    const sourceLines = Array.isArray(template?.body?.lines)
        ? template.body.lines
        : (template?.transactions || []);

    return sourceLines.map((line, index) => {
        const accountInput = line.account_ref
            ? bodyInputs.find((input) => input.name === line.account_ref)
            : null;
        const accountNumber = String(
            line.account
            || line.account_number
            || accountInput?.default
            || accountInput?.example
            || ''
        );
        const account = accounts.find((candidate) => (
            String(candidate.accountNumber || candidate.account_number || candidate.value) === accountNumber
        ));
        const matchingOption = accountInput?.options?.find((option) => (
            String(option.value) === accountNumber
        ));

        return {
            key: `${line.account_ref || accountNumber || 'account'}-${index}`,
            accountNumber,
            accountName: account?.accountName
                || account?.account_name
                || line.account_name
                || matchingOption?.label
                || '',
            context: line.transaction_description || accountInput?.label || '',
            selectable: Boolean(line.account_ref),
        };
    });
};

export const rowsFromBookkeepingTemplate = (template, accounts = []) => {
    const bodyInputs = template?.body?.inputs || [];
    const templateType = template?.body?.type || template?.template_type || null;
    const sourceLines = Array.isArray(template?.body?.lines)
        ? template.body.lines
        : (template?.transactions || []);

    let unresolvedCount = 0;
    const rows = sourceLines.map((line) => {
        const accountInput = line.account_ref
            ? bodyInputs.find((input) => input.name === line.account_ref)
            : null;
        const accountNumber = String(
            line.account
            || line.account_number
            || accountInput?.default
            || accountInput?.example
            || ''
        );
        const account = accounts.find((candidate) => (
            String(candidate.accountNumber || candidate.account_number || candidate.value) === accountNumber
        ));
        const declaredSide = line.side
            || line.transaction_type
            || (line.debit_formula ? 'debit' : line.credit_formula ? 'credit' : 'auto');
        const resolvedSide = resolveTemplatePostingSide(templateType, accountNumber, declaredSide);

        if (!account) unresolvedCount += 1;

        return {
            account_id: account?.value || account?.account_id || '',
            account_number: account?.accountNumber || account?.account_number || '',
            account_name: account?.accountName || account?.account_name || '',
            debit: '',
            credit: '',
            suggested_side: resolvedSide || 'auto',
            suggested_account_number: accountNumber,
            template_side: resolvedSide,
            template_side_rule: declaredSide,
            template_type: templateType,
        };
    });

    return { rows, unresolvedCount };
};

export const rowsFromTemplateEvaluation = (evaluation, template, accounts = []) => {
    const sourceLines = Array.isArray(evaluation?.lines) ? evaluation.lines : [];
    const templateType = template?.body?.type || template?.template_type || null;
    let unresolvedCount = 0;

    const rows = sourceLines.map((line) => {
        const accountNumber = String(line.account || line.account_number || '');
        const account = accounts.find((candidate) => (
            String(candidate.accountNumber || candidate.account_number || candidate.value) === accountNumber
        ));
        const side = line.side === 'debit' || line.side === 'credit' ? line.side : null;
        const numericAmount = Number(line.amount);
        const amount = Number.isFinite(numericAmount)
            ? numericAmount.toFixed(2).replace('.', ',')
            : '';

        if (!account) unresolvedCount += 1;

        return {
            account_id: account?.value || account?.account_id || '',
            account_number: account?.accountNumber || account?.account_number || '',
            account_name: account?.accountName || account?.account_name || line.account_name || '',
            debit: side === 'debit' ? amount : '',
            credit: side === 'credit' ? amount : '',
            suggested_side: side || 'auto',
            suggested_account_number: accountNumber,
            template_side: side,
            template_side_rule: side,
            template_type: templateType,
        };
    });

    return { rows, unresolvedCount };
};
